<template>
  <dots-menu
    v-if="this.dotsItems.length > 1"
    :items="dotsItems"
    exact-items
    is-absolute
    @selectedItem="generateFileHandler"
  >
    <template #activator="{ on }">
      <base-rounded-button v-on="on" v-bind="downloadButtonBind" />
    </template>
  </dots-menu>
  <base-rounded-button v-else v-bind="downloadButtonBind" @click="generateFileHandler(undefined, dotsItems[0])" />
</template>

<script>
import { call, get } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';

export default {
  name: 'FeatureCardFormPrintButton',
  components: { DotsMenu },
  props: {
    currentLayer: {
      type: Object,
      required: true,
    },
    featureId: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    loading: false,
    currentTaskId: null,
  }),
  computed: {
    zoom: get('map/zoom'),
    layersFilters: get('layers/layersFilters'),
    layers: get('layers/layers'),
    formPrints: get('formPrint/formPrints'),
    computedFormPrints() {
      return this.formPrints.reduce((total, current) => {
        if (current.formats.includes('docx')) {
          return [
            ...total,
            { ...current, format: 'pdf' },
            { ...current, name: `${current.name} DOCX`, format: 'docx' },
          ];
        } else {
          return [...total, current];
        }
      }, []);
    },
    dotsItems() {
      return this.computedFormPrints
        .filter(item => item.layer === this.currentLayer.id)
        .map(item => {
          return { id: item.id, name: item.name, form: item.form, format: item.format };
        });
    },
    downloadButtonBind() {
      return {
        icon: 'mdi-download',
        size: 'x-small',
        color: 'rgba(235, 235, 235, 0.5)',
        iconColor: '#202124',
        tooltip: this.$i18n.t('sidebar.generateFormPrint'),
        loading: this.loading,
      };
    },
  },
  methods: {
    ...call('formPrint/generateFormPrint*'),
    stopCeleryTask: call('celeryTasks/stopCeleryTask'),
    getMinMaxZoomStyle(style) {
      const returnStyle = JSON.parse(JSON.stringify(style));
      const { minzoom, maxzoom } = returnStyle;
      if ((minzoom || 1) > this.zoom || (maxzoom || 28) < this.zoom) {
        if (returnStyle['fill-opacity']) returnStyle['fill-opacity'] = 0;
        if (returnStyle['fill-outline-opacity']) returnStyle['fill-outline-opacity'] = 0;
      }
      const { minzoom: labelMinzoom, maxzoom: labelMaxzoom } = returnStyle.labels || {};
      if ((labelMinzoom || 1) > this.zoom || (labelMaxzoom || 28) < this.zoom) {
        if (returnStyle.labels?.attributes?.length) returnStyle.labels.attributes = [];
      }
      return returnStyle;
    },
    generateFileHandler(_, item) {
      const dict = {
        brownfields: () => this.generateFile(this.generateFormPrintScl, { form_print: item.id, sclType: item.form }),
        greenfields: () => this.generateFile(this.generateFormPrintScl, { form_print: item.id, sclType: item.form }),
        greenfields_2: () => this.generateFile(this.generateFormPrintScl, { form_print: item.id, sclType: item.form }),
        legnica: () =>
          this.generateFile(this.generateFormPrintLegnica, { form_print: item.id, fileFormat: item.format }),
        lodz: () => this.generateFile(this.generateFormPrintLodz, { form_print: item.id }),
        masterplan_2_squares: () =>
          this.generateFile(this.generateFormPrintMasterplanSquares, { form_print: item.id, isMapPrint: false }),
        masterplan_2_illuminations: () =>
          this.generateFile(this.generateFormPrintMasterplanIlluminations, { form_print: item.id, isMapPrint: false }),
        masterplan_2_streets: () => this.generateFile(this.generateFormPrintMasterplanStreets, { form_print: item.id }),
      };
      dict[item.form]();
    },
    async generateFile(fn, data = {}) {
      const { form_print, isMapPrint = true, ...params } = data;
      try {
        this.loading = true;
        const currentLayer = JSON.parse(JSON.stringify(this.layers[this.currentLayer.id]));
        const layer = {
          layer_id: currentLayer.id,
          style: currentLayer.style_web,
          labels_visible: currentLayer.labels_visible || false,
          visible: true,
          opacity: 1,
          ...currentLayer,
        };
        if (layer.style) layer.style = this.getMinMaxZoomStyle(layer.style);
        if (layer.style?.uniques) {
          Object.keys(layer.style.uniques.values).forEach(key => {
            layer.style.uniques.values[key] = this.getMinMaxZoomStyle(layer.style.uniques.values[key]);
          });
        } else if (layer.style?.ranges) {
          layer.style.ranges.values = layer.style.ranges.values.map(rangeStyle => {
            return this.getMinMaxZoomStyle(rangeStyle);
          });
        }
        layer.filter_expression = this.layersFilters[layer.layer_id + '']?.filterExpression || null;
        const printingLayerAttributes = [
          'labels_visible',
          'layer_id',
          'line_direction',
          'opacity',
          'parameters',
          'service_layers_names',
          'service_type',
          'style',
          'type',
          'url',
          'visible',
          'filter_expression',
        ];
        Object.keys(layer).forEach(key => {
          if (!printingLayerAttributes.includes(key)) delete layer[key];
        });
        const payload = {
          ...params,
          featureId: this.featureId,
          payload: { form_print },
        };
        if (isMapPrint) payload.payload.layers = [layer];
        this.currentTaskId = await fn(payload);
        this.$globalHub.$emit('handleCeleryTask', {
          currentTaskId: this.currentTaskId,
          taskStartSnackbar: {
            message: this.$i18n.t('snackbar.downloadFile'),
          },
          taskFailureSnackbar: {
            message: this.$i18n.t('snackbar.downloadFileError'),
          },
          taskFailureCallback: this.endGeneratingFileCallback,
          taskSuccessCallback: this.endGeneratingFileCallback,
        });
      } catch (error) {
        console.log('  error', error);
        this.endGeneratingFileCallback();
      }
    },
    endGeneratingFileCallback() {
      this.currentTaskId = null;
      this.loading = false;
    },
  },
  beforeDestroy() {
    if (this.currentTaskId) this.stopCeleryTask(this.currentTaskId);
  },
};
</script>
